import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { z } from 'zod';
import useHandleAppException, {
  AppExceptionResponse,
} from '../../../../../hooks/useHandleAppException';
import LangEnum from '../../../../../lang/LangEnum';
import { getAuthLoginPath } from '../../../../../routes/auth.routes';
import api from '../../../../../services/api';
import snackbarStore from '../../../../../stores/Snackbar.store';
import { useTranslation } from '../../../../translation/TranslationContext';
import { useAuthContext } from '../../../AuthContext';

const useRecoverPasswordForm = () => {
  const { translate } = useTranslation();
  const { handleErrorResponse } = useHandleAppException();
  const { loginContext } = useAuthContext();
  const history = useHistory();
  const { handleMessage } = snackbarStore;

  const schema = z.object({
    email: z
      .string()
      .trim()
      .min(1, translate(LangEnum.GLOBAL_REQUIRED))
      .pipe(z.email(translate(LangEnum.GLOBAL_INVALID_EMAIL))),
  });

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: { email: '' },
    mode: 'onBlur',
  });

  const loginPath = getAuthLoginPath(loginContext?.slug);

  const handleSubmit = form.handleSubmit(async ({ email }) => {
    const response = await api.recoverPassword({ email });

    if (response.ok) {
      handleMessage({
        type: 'success',
        title: translate(LangEnum.LOGIN_RECOVER_SUCCESS_TITLE),
        message: translate(LangEnum.LOGIN_RECOVER_SUCCESS_MESSAGE),
      });
      history.push(loginPath);
      return;
    }

    const errorMessage =
      (response.data
        ? handleErrorResponse(response as unknown as AppExceptionResponse)
        : undefined) || translate(LangEnum.GLOBAL_INVALID_EMAIL);

    handleMessage({
      type: 'error',
      title: errorMessage,
      message: translate(LangEnum.LOGIN_RECOVER_ERROR_HINT),
    });
    form.setError('email', { message: errorMessage });
  });

  return { form, handleSubmit, loginPath };
};

export default useRecoverPasswordForm;
